import { useCallback, useEffect, useState } from "react";

const TICK_MS = 250;

export interface CooldownState {
  /** Seconds remaining before the action is available again; 0 when available. */
  secondsLeft: number;
  /** True while the action must stay disabled. */
  isCooling: boolean;
  /** Starts (or restarts) the cooldown from its full duration. */
  start: () => void;
}

export interface CooldownOptions {
  /**
   * localStorage key holding the deadline. When given, the cooldown survives a
   * reload, so the countdown cannot be skipped by refreshing the page.
   */
  storageKey?: string;
}

const secondsUntil = (deadline: number) =>
  Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

/** Returns the stored deadline only while it is still in the future. */
const readDeadline = (storageKey: string | undefined): number | null => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const deadline = Number(raw);
    return Number.isFinite(deadline) && deadline > Date.now() ? deadline : null;
  } catch {
    // Storage can throw in private browsing; a missing cooldown is recoverable.
    return null;
  }
};

const writeDeadline = (storageKey: string | undefined, deadline: number) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, String(deadline));
  } catch {
    /* noop */
  }
};

const clearDeadline = (storageKey: string | undefined) => {
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* noop */
  }
};

/** True while a cooldown persisted by an earlier page load is still running. */
export const isStoredCooldownActive = (storageKey: string | undefined): boolean =>
  readDeadline(storageKey) !== null;

/**
 * Counts down from `durationSeconds` after `start()` is called.
 * Idle until triggered, so an action is available on first render.
 *
 * Remaining time is derived from a deadline rather than accumulated ticks, so
 * the countdown stays accurate even if timers are throttled in a background tab,
 * and can be restored from storage after a reload.
 */
const useCooldown = (
  durationSeconds: number,
  { storageKey }: CooldownOptions = {},
): CooldownState => {
  const [deadline, setDeadline] = useState<number | null>(() =>
    readDeadline(storageKey),
  );
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const stored = readDeadline(storageKey);
    return stored === null ? 0 : secondsUntil(stored);
  });

  const start = useCallback(() => {
    const next = Date.now() + durationSeconds * 1000;
    writeDeadline(storageKey, next);
    setDeadline(next);
    setSecondsLeft(durationSeconds);
  }, [durationSeconds, storageKey]);

  // The key depends on the signed-in user, which can arrive after first render.
  useEffect(() => {
    if (deadline !== null) return;
    const stored = readDeadline(storageKey);
    if (stored === null) return;
    setDeadline(stored);
    setSecondsLeft(secondsUntil(stored));
  }, [storageKey, deadline]);

  useEffect(() => {
    if (deadline === null) return;

    const id = setInterval(() => {
      const left = secondsUntil(deadline);
      setSecondsLeft(left);
      if (left <= 0) {
        clearDeadline(storageKey);
        setDeadline(null);
      }
    }, TICK_MS);

    return () => clearInterval(id);
  }, [deadline, storageKey]);

  return { secondsLeft, isCooling: secondsLeft > 0, start };
};

export default useCooldown;
